import { redirect } from '@tanstack/react-router'
import { OrderBy, type BaseFilterBy } from '@customTypes/cloudmix/api'
import { pruneFilterBy } from '@lib/listStateNormalizer'
import type { ListStateService } from '@lib/listState/service'
import {
  buildListDefaults,
  hasListSearch,
  ListSearchFields,
  normalizeOrderBy,
  parseSearchNumber,
  toSparseListState,
  type ListState,
} from '@lib/listState/queryState'

type HydrateListSearchBeforeLoadOptions<
  F extends Record<string, unknown> = BaseFilterBy,
  S extends ListSearchFields = ListSearchFields,
> = {
  search: S
  listState: ListStateService
  orgId?: string
  routeId: string
  scope?: string
  tableId?: string
  defaultSort?: OrderBy[]
  defaultFilterBy?: F
  allowedOrderByFields?: readonly string[]
}

export function hydrateListSearchBeforeLoad<
  F extends Record<string, unknown> = BaseFilterBy,
  S extends ListSearchFields = ListSearchFields,
>({
  search,
  listState,
  orgId,
  routeId,
  scope,
  tableId,
  defaultSort,
  defaultFilterBy,
  allowedOrderByFields,
}: HydrateListSearchBeforeLoadOptions<F, S>) {
  if (hasListSearch(search)) {
    return
  }

  const storageKey = listState.buildKey({
    orgId,
    routeId,
    scope,
    tableId,
  })
  const storedState = listState.get<ListState<F>>(storageKey)
  if (!storedState) {
    return
  }

  const allowedOrderByFieldSet =
    allowedOrderByFields && allowedOrderByFields.length > 0
      ? new Set(allowedOrderByFields)
      : null

  const defaults = buildListDefaults<F>({
    defaultSort,
    defaultFilterBy,
    allowedOrderByFieldSet,
  })

  const hydratedState: ListState<F> = {
    pageIndex: storedState.pageIndex ?? defaults.pageIndex,
    pageSize: storedState.pageSize ?? defaults.pageSize,
    orderBy:
      normalizeOrderBy(storedState.orderBy, allowedOrderByFieldSet) ??
      defaults.orderBy,
    filterBy: pruneFilterBy(storedState.filterBy) ?? defaults.filterBy,
  }

  const sparse = toSparseListState(hydratedState, defaults)
  if (Object.keys(sparse).length === 0) {
    return
  }

  throw redirect({
    to: '.',
    replace: true,
    search: (prev) => ({
      ...prev,
      pageIndex: parseSearchNumber(sparse.pageIndex) ?? undefined,
      pageSize: parseSearchNumber(sparse.pageSize) ?? undefined,
      orderBy: sparse.orderBy,
      filterBy: sparse.filterBy,
    }),
  })
}
